import { useState, useEffect } from 'react'
import { Plus, Trash2, Copy, Check } from 'lucide-react'
import { fetchInvites, createInvite, deleteInvite } from '../../services/admin'
import type { AdminInvite } from '../../services/admin'

export default function InvitesPanel() {
  const [invites, setInvites] = useState<AdminInvite[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Form state
  const [formEmail, setFormEmail] = useState('')
  const [formMaxUses, setFormMaxUses] = useState(0)
  const [formExpiresInDays, setFormExpiresInDays] = useState(0)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    loadInvites()
  }, [])

  async function loadInvites() {
    setLoading(true)
    try {
      const data = await fetchInvites()
      setInvites(data)
    } catch {
      // Error handled
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate() {
    setCreating(true)
    try {
      await createInvite({
        email: formEmail || undefined,
        maxUses: formMaxUses,
        expiresInDays: formExpiresInDays > 0 ? formExpiresInDays : undefined,
      })
      setShowForm(false)
      setFormEmail('')
      setFormMaxUses(0)
      setFormExpiresInDays(0)
      await loadInvites()
    } catch {
      // Error handled
    } finally {
      setCreating(false)
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this invite token? This cannot be undone.')) return
    try {
      await deleteInvite(id)
      setInvites(prev => prev.filter(i => i.id !== id))
    } catch {
      // Error handled
    }
  }

  function copyToken(invite: AdminInvite) {
    navigator.clipboard.writeText(invite.token)
    setCopiedId(invite.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  if (loading) {
    return <p className="font-mono text-sm text-slate-500 animate-pulse">Loading invites...</p>
  }

  return (
    <div className="space-y-4">
      {/* Create button / form */}
      {showForm ? (
        <div className="glass rounded-xl p-4 space-y-3">
          <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Create Invite</h3>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">
                Email (optional)
              </label>
              <input
                type="email"
                value={formEmail}
                onChange={e => setFormEmail(e.target.value)}
                placeholder="restrict to email"
                className="w-full bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-400 outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">
                Max uses (0 = unlimited)
              </label>
              <input
                type="number"
                value={formMaxUses}
                onChange={e => setFormMaxUses(parseInt(e.target.value) || 0)}
                min={0}
                className="w-20 bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 focus:border-cyan-400 outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">
                Expires (days)
              </label>
              <input
                type="number"
                value={formExpiresInDays}
                onChange={e => setFormExpiresInDays(parseInt(e.target.value) || 0)}
                min={0}
                placeholder="0 = never"
                className="w-24 bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-400 outline-none"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={creating}
              className="px-3 py-1.5 rounded-lg font-mono text-xs bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/25 transition-all disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-3 py-1.5 rounded-lg font-mono text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg font-mono text-xs bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/25 transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Invite
        </button>
      )}

      {/* Invites table */}
      <p className="font-mono text-xs text-slate-500">{invites.length} invites</p>
      {invites.length === 0 ? (
        <p className="font-mono text-xs text-slate-600">No invites yet</p>
      ) : (
        <div className="glass rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-cyan-500/10">
                <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Token</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Email</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Uses</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Expires</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Created</th>
                <th className="w-20 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {invites.map(invite => (
                <tr key={invite.id} className="border-b border-white/[0.06] last:border-0">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <code className="font-mono text-xs text-slate-200">
                        {invite.token.slice(0, 8)}...
                      </code>
                      <button
                        onClick={() => copyToken(invite)}
                        className="p-1 text-slate-500 hover:text-cyan-400 transition-colors"
                        title="Copy token"
                      >
                        {copiedId === invite.id ? (
                          <Check className="w-3 h-3 text-emerald-400" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-400">
                    {invite.email ?? '-'}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-400 tabular-nums">
                    {invite.uses_count}/{invite.max_uses ?? '\u221e'}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-500">
                    {invite.expires_at
                      ? new Date(invite.expires_at).toLocaleDateString()
                      : 'never'}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-500">
                    {new Date(invite.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      onClick={() => handleDelete(invite.id)}
                      className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                      title="Delete invite"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
